import { useEffect, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure } from "../../../telemetry";
import { searchPeople, type Person } from "../services/people";

/** Wait this long after the last keystroke before searching. */
export const SEARCH_DELAY_MS = 250;
/** Shorter queries aren't searched. One letter is enough, as in Outlook's own picker. */
export const MIN_QUERY_LENGTH = 1;

interface SearchState {
  /** The query and whether searching was on, for the results held. */
  readonly key: string;
  readonly results: readonly Person[];
  /** The directory couldn't be searched (for example, no User.ReadBasic.All). */
  readonly failed: boolean;
}

/**
 * Directory search for adding an attendee. `enabled` is false while the
 * suggestions from your own meetings are enough (Outlook's "Show more results"
 * turns it on).
 */
export function usePeopleSearch(token: string | null, query: string, enabled = true) {
  const { refreshGraphToken } = useAuthState();
  const [state, setState] = useState<SearchState>({ key: "", results: [], failed: false });
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const q = query.trim();
  const active = enabled && q.length >= MIN_QUERY_LENGTH;
  const key = `${active}|${q}`;

  useEffect(() => {
    const current = tokenRef.current;
    if (!active || !current) {
      setState({ key, results: [], failed: false });
      return;
    }
    let cancelled = false;
    const id = window.setTimeout(() => {
      searchPeople(current, () => refreshGraphToken(false), q)
        .then((results) => {
          if (!cancelled) setState({ key, results, failed: false });
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setState({ key, results: [], failed: true });
          logConnectorFailure("calendar", e, "search-people");
        });
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [key, active, q, refreshGraphToken]);

  const settled = state.key === key;
  return {
    results: settled ? state.results : [],
    failed: settled && state.failed,
    searching: active && !settled,
  };
}
